import { FreeExpertPick_ExpertPickFragment, GameTeam, Maybe, SportslineWebComponent_FreeExpertPickQuery } from '@lib/graphql-types';
import { clients } from 'src/config';
import { ClientName, SportslinePickProps } from 'src/types';
import { getLeagueRoutePath } from './gameUtils';
import { toSides } from './pickSnippet';
import { isUndefinedOrNull } from './commonUtils';
import { BetSelectionEntity, PickSnippetEventViewed } from './trackingUtils';

export function selectionToPickCardSelectionProps({ selection }: SportslineWebComponent_FreeExpertPickQuery['freeExpertPick']): SportslinePickProps['selection'] | undefined {
  if (!selection || !('__typename' in selection)) return undefined;
  switch (selection.__typename) {
    case 'ExpertPickSelectionPlayerProps':
      return {
        pickType: 'playerProps',
        label: selection.label,
        subLabel: selection.subLabel,
        odds: selection.odds.toString(),
        side: selection.side,
        player: {
          id: selection.player.id,
          name: `${selection.player.firstName} ${selection.player.lastName}`,
          teamId: selection.playerTeam.id,
        },
      };
    case 'ExpertPickSelectionGameProps':
      return {
        pickType: 'gameProp',
        label: selection.label,
        subLabel: selection.subLabel,
        odds: selection.odds.toString(),
        side: selection.side,
        ...(Boolean(selection.team) && {
          team: {
            id: selection.team.id,
            name: selection.team.mediumName,
            abbrev: selection.team.abbrev,
            color: selection.team.colorHexDex,
          },
          ...(['OVER', 'UNDER'].some(v => v === selection.side) && {
            overUnder: selection.side,
          }),
        }),
      };
    case 'ExpertPickSelectionTeamProps':
      return {
        pickType: 'teamProp',
        label: selection.label,
        subLabel: selection.subLabel,
        odds: selection.odds.toString(),
        side: selection.side,
        ...(Boolean(selection.team) && {
          team: {
            id: selection.team.id,
            name: selection.team.mediumName,
            abbr: selection.team.abbrev,
            color: selection.team.colorHexDex,
          },
        }),
        overUnder: selection.side,
      };
    case 'ExpertPickSelectionStandard':
      return {
        pickType: 'standard',
        label: selection.label,
        subLabel: selection.subLabel,
        odds: selection.odds.toString(),
        side: selection.side,
        ...(Boolean(selection.team) && {
          team: {
            id: selection.team.id,
            name: selection.team.mediumName,
            abbr: selection.team.abbrev,
            color: selection.team.colorHexDex,
          },
        }),
        ...(selection.marketType === 'OVER_UNDER' && {
          overUnder: selection.side,
        }),
      };
    default:
      return undefined;
  }
}

export function getResultStatusText(resultStatus: string): SportslinePickProps['status'] {
  switch ((resultStatus || '').toLowerCase()) {
    case 'won':
    case 'win':
      return 'win';
    case 'loss':
      return 'loss';
    case 'pushed':
    case 'push':
      return 'push';
    default:
      return 'pending';
  }
}

export function getTeamName(team: GameTeam): string {
  return team.mediumName ?? team.nickname ?? team.location;
}

export function fromExpertPickToSportslinePickProps(expertPick: FreeExpertPick_ExpertPickFragment): SportslinePickProps {
  const selection = selectionToPickCardSelectionProps(expertPick);
  return {
    id: expertPick.id,
    gameInfo: expertPick.game,
    status: getResultStatusText(expertPick.resultStatus),
    analysis: expertPick.writeup,
    createdAt: expertPick.createdAt,
    unit: expertPick.selection?.unit,
    expert: expertPick.expert,
    selection,
    expertStreaks: expertPick.expertStreaks,
  };
}

export function fromSportslineWebComponent_FreeExpertPickQueryToSportslinePickProps(data: SportslineWebComponent_FreeExpertPickQuery): SportslinePickProps {
  const pick = data.freeExpertPick;
  if (!Boolean(pick)) {
    return null;
  }
  return fromExpertPickToSportslinePickProps(pick);
}

type GetPropertySubscribeLinkArgs = {
  subscribeLink: string;
  clientName: ClientName;
};

export function getPropertySubscribeLink({ subscribeLink, clientName }: GetPropertySubscribeLinkArgs): string {
  if (Boolean(subscribeLink)) {
    try {
      return new URL(subscribeLink).toString();
    } catch (error) {
      console.warn('Invalid URL:', error);
    }
  }
  return clients[clientName]?.subscribeLink ?? clients.sportsline.subscribeLink;
}

type GetPropertyGameHrefArgs = {
  clientName: ClientName;
  leagueAbbr: SportslinePickProps['gameInfo']['league']['abbrev'];
  gameAbbr: SportslinePickProps['gameInfo']['abbrev'];
};

export function getPropertyGameHref({ leagueAbbr, gameAbbr }: GetPropertyGameHrefArgs): string | null {
  const league = getLeagueRoutePath(leagueAbbr);
  return clients.sportsline.gameHref(league, gameAbbr);
}

type GetPropertyUpsellTextArgs = {
  clientName: ClientName;
  upsellText?: string;
};

export function getPropertyUpsellText({ clientName, upsellText }: GetPropertyUpsellTextArgs): string {
  if (Boolean(upsellText)) {
    return upsellText;
  }
  return clients[clientName]?.upsellText ?? clients.sportsline.upsellText;
}

export function getCardTitle({ title, leagueAbbr = '' }: { title?: string; leagueAbbr: Maybe<SportslinePickProps['gameInfo']['league']['abbrev']> }): string {
  const _leagueAbbr = leagueAbbr?.toUpperCase();
  return Boolean(title) ? title : `SPORTSLINE’S${Boolean(_leagueAbbr) ? ` ${_leagueAbbr} ` : ` `}PICK OF THE DAY`;
}

export function getPropertyGameUrlTarget(clientName: ClientName): '_blank' | '_self' {
  return clientName === 'cbssports' ? '_blank' : '_self';
}

export const fromFreeExpertPickToTrackingViewedWidgetDataBet = (
  pick: SportslineWebComponent_FreeExpertPickQuery['freeExpertPick'],
): PickSnippetEventViewed['widgetData']['bets'][0] => {
  const league = pick.game?.league?.abbrev;
  const selectionLabel = pick.selection?.labelLong;
  const line = pick.selection?.value;
  
  const odds = pick.selection?.odds;
  const side = pick.selection?.side;

  const entity = getEntityFromSelection(pick.selection);

  return {
    gameId: pick.game?.id,
    position: -1, // Position is not provided in the FreeExpertPick
    league,
    market: {
      id: pick.market?.id ? Number(pick.market.id) : null,
      label: pick.market?.name ? pick.market.name.toLocaleLowerCase() : null,
    },
    selection: {
      label: selectionLabel,
      odds,
      side: toSides(side?.toLowerCase()),
      // check if has line
      ...(!isUndefinedOrNull(line) && {
        line,
      }),
      // check if has entity
      ...(!isUndefinedOrNull(entity) && {
        entity
      }),
    },
  };
};

export function getEntityFromSelection(
  selection: {
    __typename?: string;
    team?: { id?: string | number };
    player?: { id?: string | number };
  } | null | undefined
): BetSelectionEntity | null {
  switch (selection?.__typename) {
    case 'ExpertPickSelectionStandard':
    case 'ExpertPickSelectionTeamProps':
    case 'ExpertPickSelectionGameProps':
      if (isUndefinedOrNull(selection.team?.id)) {
        return null;
      }
     return {
        type: 'team',
        id: selection.team.id.toString(),
      };
    case 'ExpertPickSelectionPlayerProps':
      if (isUndefinedOrNull(selection.player?.id)) {
        return null;
      }
      return {
        type: 'player',
        id: selection.player.id.toString(),
      };
    default:
      return null;
  }
}
